// Feedback-related type definitions

export interface FeedbackCriterion {
  id: string;
  title: string;
  description: string;
  rating: number | null;
}

export interface FeedbackForm {
  sessionId: string;
  fromUserId: string;
  toUserId: string;
  criteria: FeedbackCriterion[];
  overallRating: number;
  comment: string;
  subject: string;
}

export interface FeedbackSubmission extends Omit<FeedbackForm, "criteria"> {
  individualRatings: Record<string, number>;
}

export interface StudentEvaluation {
  sessionId: string;
  studentId: string;
  tutorId: string;
  criteria: {
    understanding: number;
    problemSolving: number;
    retention: number;
    improvement: number;
    confidence: number;
    engagement: number;
    preparedness: number;
    curiosity: number;
    effort: number;
    attitude: number;
  };
  comment: string;
  createdAt: Date;
}

// Default criteria for student evaluation
export const DEFAULT_STUDENT_CRITERIA: Omit<FeedbackCriterion, "rating">[] = [
  {
    id: "understanding",
    title: "The student demonstrated good understanding of the concepts explained",
    description: "The student grasped the core and essential knowledge during our tutoring",
  },
  {
    id: "problemSolving",
    title: "The student effectively applied learned concepts to solve problems",
    description: "The student successfully uses new knowledge in numerical applications",
  },
  {
    id: "retention",
    title: "The student remembered and built upon content from previous sessions",
    description: "The student showed good memory and understanding from previous sessions",
  },
  {
    id: "improvement",
    title: "The student showed noticeable improvement during the session",
    description: "There was visible progress and improvement during the lesson",
  },
  {
    id: "confidence",
    title: "The student appeared confident and self-assured with the material",
    description: "The student showed confidence and belief in their abilities",
  },
  {
    id: "engagement",
    title: "The student was actively engaged and participated throughout the session",
    description: "The student showed involvement and active participation in the learning process",
  },
  {
    id: "preparedness",
    title: "The student came well-prepared with materials and completed assignments",
    description: "The student was organized and ready for the learning session",
  },
  {
    id: "curiosity",
    title: "The student asked thoughtful questions and showed curiosity to learn",
    description: "The student demonstrated how much they desire to learn and improve",
  },
  {
    id: "effort",
    title: "The student showed strong effort and dedication during the session",
    description: "The student showed how much work and effort they are putting in",
  },
  {
    id: "attitude",
    title: "The student has strong learning potential and a positive attitude",
    description: "Overall assessment of the student's capability and approach to learning",
  },
];

// Default criteria for tutor evaluation (based on the image)
export const DEFAULT_TUTOR_CRITERIA: Omit<FeedbackCriterion, "rating">[] = [
  {
    id: "expertise",
    title: "The tutor demonstrated strong expertise and mastery of the subject matter",
    description: "The tutor showed comprehensive knowledge and understanding of the topics covered",
  },
  {
    id: "explanations",
    title: "The tutor provided clear, organized, and understandable explanations",
    description: "Instructions and explanations were easy to follow and well-structured",
  },
  {
    id: "teachingStrategies",
    title: "The tutor used effective teaching strategies and learning approaches",
    description: "Teaching methods were appropriate and helped facilitate my understanding",
  },
  {
    id: "engagement",
    title: "The tutor kept me engaged and motivated throughout the session",
    description: "I felt actively involved and interested during the entire tutoring session",
  },
  {
    id: "timeManagement",
    title: "The tutor effectively utilized and structured the session time",
    description: "Time was well-managed with appropriate pacing and coverage of material",
  },
  {
    id: "professionalism",
    title: "The tutor maintained professional, respectful, and appropriate behavior",
    description: "The tutor's conduct was consistently professional throughout the session",
  },
  {
    id: "communication",
    title: "The tutor communicated effectively and responded well to my questions",
    description: "Communication was clear, and my questions were addressed satisfactorily",
  },
  {
    id: "patience",
    title: "The tutor was patient and supportive when I struggled with concepts",
    description: "I felt supported and encouraged, especially during challenging moments",
  },
  {
    id: "preparation",
    title: "The tutor was well-prepared with materials and lesson structure",
    description: "The session was organized with appropriate materials and clear structure",
  },
  {
    id: "satisfaction",
    title: "I am satisfied with the overall quality of this tutoring session",
    description: "The session met my expectations and learning needs effectively",
  },
];
